import { Loader2 } from 'lucide-react';
import {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import FaceOval from 'assets/face_oval.png';
import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { getMedsiScanConfig } from 'lib/medsi/config';
import type { MedsiScanVitalSigns } from 'lib/medsi/scan-compat';
import { cn } from 'lib/utils';
import { InfoData } from '../../../../types';
import { CircularTimer } from './components/CircularTimer';
import { ScanInfoCard } from './components/ScanInfoCard';
import { ScanLiveVitals } from './components/ScanLiveVitals';

const PREVIEW_ASPECT = 0.8067;
const OVAL_BORDER_COLOR = '#1A80D9';
const TIMER_INSET = 22;
const INFO_GAP = 8;
const BUTTON_HEIGHT = 48;
const CHROME_RESERVE = INFO_GAP + 64 + BUTTON_HEIGHT + 24;

interface MedsiScanProps {
  info: InfoData;
  videoRef: RefObject<HTMLVideoElement>;
  vitalSigns: MedsiScanVitalSigns | null;
  isCameraReady: boolean;
  isMeasuring: () => boolean;
  isStopping?: boolean;
  onMeasure: () => void;
  scanProgress?: number;
  scanError?: string;
}

const ScanPreview = memo(function ScanPreview({
  videoRef,
  measuring,
  borderColor,
  duration,
  progress,
  ovalW,
  ovalH,
  frameW,
  frameH,
}: {
  videoRef: RefObject<HTMLVideoElement>;
  measuring: boolean;
  borderColor: string;
  duration: number;
  progress: number;
  ovalW: number;
  ovalH: number;
  frameW: number | undefined;
  frameH: number | undefined;
}) {
  const clipStyle = {
    clipPath: 'ellipse(49.5% 49.5% at 50% 50%)',
    WebkitClipPath: 'ellipse(49.5% 49.5% at 50% 50%)',
  } as const;

  return (
    <div className='relative flex items-center justify-center w-full shrink-0 overflow-visible'>
      <div
        className='relative shrink-0 overflow-visible'
        style={{
          width: frameW ?? '100%',
          height: frameH,
          aspectRatio: frameH ? undefined : String(PREVIEW_ASPECT),
          maxHeight: '100%',
        }}
      >
        <div
          className='absolute overflow-hidden bg-black'
          style={
            ovalW > 0
              ? {
                  left: TIMER_INSET,
                  top: TIMER_INSET,
                  width: ovalW,
                  height: ovalH,
                  willChange: 'transform',
                  ...clipStyle,
                }
              : { inset: TIMER_INSET, willChange: 'transform', ...clipStyle }
          }
        >
          <video
            ref={videoRef}
            id='video'
            muted
            playsInline
            className='absolute inset-0 object-cover w-full h-full scale-x-[-1] transform pointer-events-none'
          />
          <img
            src={FaceOval}
            alt=''
            aria-hidden
            className='absolute inset-0 z-[1] object-fill w-full h-full pointer-events-none'
          />
        </div>

        {measuring && ovalW > 0 ? (
          <div
            className='absolute overflow-visible pointer-events-none'
            style={{
              left: TIMER_INSET,
              top: TIMER_INSET,
              width: ovalW,
              height: ovalH,
            }}
          >
            <CircularTimer
              width={ovalW}
              height={ovalH}
              progress={progress}
              strokeWidth={4}
              strokeColor='white'
              ellipseBorderColor={borderColor}
              duration={duration}
              isActive={measuring}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
});

function MedsiScan({
  info,
  videoRef,
  vitalSigns,
  isCameraReady,
  isMeasuring,
  isStopping = false,
  onMeasure,
  scanProgress = 0,
  scanError,
}: MedsiScanProps) {
  const { t } = useTranslation();
  const duration = getMedsiScanConfig().duration;
  const measuring = isMeasuring();
  const showLiveVitals = measuring && scanProgress * duration >= 30;
  const rootRef = useRef<HTMLDivElement>(null);
  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });

  const updatePreviewSize = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;

    const { width: rootW, height: rootH } = root.getBoundingClientRect();
    if (rootW <= 0 || rootH <= 0) return;

    const availableW = Math.max(0, rootW - TIMER_INSET * 2);
    const availableH = Math.max(0, rootH - CHROME_RESERVE - TIMER_INSET * 2);
    if (availableW <= 0 || availableH <= 0) return;

    let width = availableW;
    let height = width / PREVIEW_ASPECT;
    if (height > availableH) {
      height = availableH;
      width = height * PREVIEW_ASPECT;
    }

    setPreviewSize(prev =>
      Math.abs(prev.width - width) < 0.5 && Math.abs(prev.height - height) < 0.5
        ? prev
        : { width, height },
    );
  }, []);

  useEffect(() => {
    updatePreviewSize();
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => updatePreviewSize());
    ro.observe(root);
    window.addEventListener('resize', updatePreviewSize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updatePreviewSize);
    };
  }, [updatePreviewSize]);

  const errorMessage =
    (measuring && info?.message ? info.message : undefined) ?? scanError;
  const borderColor = measuring && info?.message ? 'red' : OVAL_BORDER_COLOR;

  const ovalW = previewSize.width;
  const ovalH = previewSize.height;
  const frameW = ovalW > 0 ? ovalW + TIMER_INSET * 2 : undefined;
  const frameH = ovalH > 0 ? ovalH + TIMER_INSET * 2 : undefined;

  return (
    <div className='h-[calc(100svh-4rem)] max-h-[calc(100svh-4rem)] overflow-hidden px-4 pt-1 pb-[max(1rem,env(safe-area-inset-bottom))]'>
      <div
        ref={rootRef}
        className='flex flex-col flex-1 w-full max-w-3xl h-full mx-auto min-h-0'
      >
        <ScanPreview
          videoRef={videoRef}
          measuring={measuring}
          borderColor={borderColor}
          duration={duration}
          progress={scanProgress}
          ovalW={ovalW}
          ovalH={ovalH}
          frameW={frameW}
          frameH={frameH}
        />

        <ScrollArea
          className='flex-1 w-full min-h-0'
          style={{ marginTop: INFO_GAP }}
        >
          <div className='flex flex-col w-full gap-2 pr-1 md:gap-3'>
            <ScanInfoCard measuring={measuring} errorMessage={errorMessage} />
            {showLiveVitals ? (
              <ScanLiveVitals vitalSigns={vitalSigns} />
            ) : null}
          </div>
        </ScrollArea>

        {!showLiveVitals ? (
          <Button
            type='button'
            onClick={onMeasure}
            disabled={isStopping || (!measuring && !isCameraReady)}
            className={cn(
              'mx-4 shrink-0',
              measuring && 'bg-red-600 hover:bg-red-700 text-white',
            )}
          >
            {isStopping ? (
              <>
                <Loader2 className='w-4 h-4 mr-2 animate-spin' aria-hidden />
                {t('medsi_stopping')}
              </>
            ) : measuring ? (
              t('stop')
            ) : isCameraReady ? (
              t('medsi_measure_now')
            ) : (
              t('medsi_preparing_camera')
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default memo(MedsiScan);
